import { ReactNode, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '../Icon.js';
import { WithTooltip } from './Tooltip.js';
import { Dropdown, DropdownProps } from './Dropdown.js';

// ── FilterRow root ────────────────────────────────────────────────────────────

interface FilterRowProps {
  children: ReactNode;
  borderBottom?: boolean;
  /** Ancla del recorrido guiado (Spotlight Pattern). Identidad, no estilo: la
   *  REGLA #16 prohíbe className y style, no un punto de anclaje. Va acá y no
   *  en un div envolvente porque un wrapper con `display: contents` no tiene
   *  caja y no se puede medir, y uno con caja rompe el flex de la fila. */
  tourId?: string;
}

function FilterRowRoot({ children, borderBottom = true, tourId }: FilterRowProps) {
  return (
    <div
      data-tour-id={tourId}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--evo-space-2)',
        padding: `0 var(--evo-content-pad-x) 0 var(--evo-page-pad-x)`,
        height: 'var(--evo-subnav-h)',
        flexShrink: 0,
        borderBottom: borderBottom ? '1px solid var(--evo-stroke)' : 'none',
      }}
    >
      {children}
    </div>
  );
}

// ── Search ────────────────────────────────────────────────────────────────────

interface SearchProps {
  value: string;
  onChange: (value: string) => void;
  onSubmit?: () => void;
  onClear?: () => void;
  placeholder?: string;
  width?: number;
  /** Ocupa el ancho disponible de la fila en vez del --evo-search-w fijo
   *  (280px). Excepción documentada — REGLA #7 sigue aplicando para
   *  módulos de lista estándar; esto es para pantallas de búsqueda libre
   *  (ej. Historial de Evo Brain) donde el ancho fijo se ve cortado. */
  fullWidth?: boolean;
}

function Search({ value, onChange, onSubmit, onClear, placeholder, width, fullWidth = false }: SearchProps) {
  const { t } = useTranslation();
  const resolvedPlaceholder = placeholder ?? t('common.search');
  const inputRef = useRef<HTMLInputElement>(null);

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0', flexShrink: fullWidth ? 1 : 0, flexGrow: fullWidth ? 1 : 0, minWidth: 0 }}>
      <input
        ref={inputRef}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') onSubmit?.(); }}
        placeholder={resolvedPlaceholder}
        style={{
          height: 'var(--evo-search-h)',
          width: fullWidth ? '100%' : (width !== undefined ? `${width}px` : 'var(--evo-search-w)'),
          minWidth: 0,
          padding: '0 10px',
          background: 'var(--evo-bg-2)',
          border: '1px solid var(--evo-stroke)',
          borderRight: 'none',
          borderRadius: 'var(--evo-r-sm) 0 0 var(--evo-r-sm)',
          color: 'var(--evo-fg-2)',
          fontFamily: 'var(--evo-font)',
          fontSize: 'var(--evo-fs-body)',
          outline: 'none',
        }}
      />
      <WithTooltip label={t('common.search')} align="left">
        <button
          type="button"
          title={t('common.search')}
          onClick={() => onSubmit ? onSubmit() : inputRef.current?.focus()}
          style={{
            height: 'var(--evo-control-h)', width: 'var(--evo-control-h)', flexShrink: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: 'var(--evo-bg-2)',
            border: '1px solid var(--evo-stroke)',
            borderRadius: '0 var(--evo-r-sm) var(--evo-r-sm) 0',
            color: 'var(--evo-cyan)', cursor: onSubmit ? 'pointer' : 'default',
          }}
        >
          <Icon name="search" size={14} />
        </button>
      </WithTooltip>
      {onClear && value && (
        <WithTooltip label={t('common.remove')} align="left">
          <button
            type="button"
            title={t('common.remove')}
            onClick={onClear}
            style={{
              marginLeft: '4px', height: 'var(--evo-icon-btn-sm)', width: 'var(--evo-icon-btn-sm)', flexShrink: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: 'transparent', border: 'none',
              color: 'var(--evo-fg-3)', cursor: 'pointer',
            }}
          >
            <Icon name="close" size={12} />
          </button>
        </WithTooltip>
      )}
    </div>
  );
}

// ── Dropdown wrapper ──────────────────────────────────────────────────────────

function FilterDropdown<T extends string | number>(props: DropdownProps<T>) {
  return <Dropdown<T> size="sm" {...props} />;
}

// ── Active filter chip ────────────────────────────────────────────────────────

interface ChipProps {
  label: string;
  onRemove: () => void;
}

function Chip({ label, onRemove }: ChipProps) {
  const { t } = useTranslation();
  return (
    <div
      style={{
        display: 'inline-flex', alignItems: 'center', gap: '4px',
        height: '24px', padding: '0 8px',
        background: 'var(--evo-cyan-12)',
        border: '1px solid var(--evo-cyan-18)',
        borderRadius: 'var(--evo-r-pill)',
        color: 'var(--evo-cyan)',
        fontFamily: 'var(--evo-font)', fontSize: 'var(--evo-fs-caption)', fontWeight: 600,
        flexShrink: 0,
      }}
    >
      {label}
      <button
        type="button"
        title={t('common.remove')}
        aria-label={t('common.remove')}
        onClick={onRemove}
        style={{
          display: 'flex', alignItems: 'center', background: 'none',
          border: 'none', padding: 0, cursor: 'pointer',
          color: 'var(--evo-cyan)', lineHeight: 1,
        }}
      >
        <Icon name="close" size={10} />
      </button>
    </div>
  );
}

// ── Spacer ────────────────────────────────────────────────────────────────────

function Spacer() {
  return <div style={{ flex: 1 }} />;
}

// ── Action link ───────────────────────────────────────────────────────────────

interface ActionProps {
  label: string;
  onClick: () => void;
  tone?: 'muted' | 'cyan';
}

function Action({ label, onClick, tone = 'muted' }: ActionProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        background: 'none', border: 'none', padding: '0 4px',
        cursor: 'pointer', flexShrink: 0,
        color: tone === 'cyan' ? 'var(--evo-cyan)' : 'var(--evo-fg-3)',
        fontFamily: 'var(--evo-font)', fontSize: '12px',
      }}
    >
      {label}
    </button>
  );
}

// ── Compose export ────────────────────────────────────────────────────────────

export const FilterRow = Object.assign(FilterRowRoot, {
  Search,
  Dropdown: FilterDropdown,
  Chip,
  Spacer,
  Action,
});
